import { supabase } from "@/integrations/supabase/client";

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: string;
  price: number;
  instructor_name: string;
  rating: number;
  students_count: number;
  thumbnail_url: string;
  is_coming_soon?: boolean;
  estimated_launch_date?: string;
  early_bird_discount?: number;
  discount_percentage?: number;
}

interface CachedData<T> {
  data: T;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, CachedData<any>>();

function getCached<T>(key: string): T | null {
  const cached = cache.get(key);
  if (!cached) return null;
  
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

export async function fetchAllCourses(): Promise<Course[]> {
  const cacheKey = "all_courses";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;
  
  const { data, error } = await supabase
    .from("courses")
    .select("*")
    .eq("is_published", true);
  
  if (error) throw error;
  
  const courses = data || [];
  setCache(cacheKey, courses);
  return courses;
}

export async function fetchPopularCourses(): Promise<Course[]> {
  const cacheKey = "popular_courses";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;
  
  // Simplified query - just get top courses by students_count
  const { data, error } = await supabase
    .from("courses")
    .select("*")
    .eq("is_published", true)
    .order("students_count", { ascending: false })
    .limit(6);
  
  if (error) throw error;
  
  const courses = data || [];
  setCache(cacheKey, courses);
  return courses;
}

export async function fetchFeaturedCourses(): Promise<Course[]> {
  const cacheKey = "featured_courses";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;
  
  const { data, error } = await supabase
    .from("courses")
    .select("*")
    .eq("is_published", true)
    .order("rating", { ascending: false })
    .limit(3);
  
  if (error) throw error;
  
  const courses = data || [];
  setCache(cacheKey, courses);
  return courses;
}

export async function fetchCategories(): Promise<string[]> {
  const cacheKey = "categories";
  const cached = getCached<string[]>(cacheKey);
  
  if (cached) return cached;
  
  const { data, error } = await supabase
    .from("course_categories")
    .select("name")
    .eq("is_active", true)
    .order("display_order", { ascending: true });
  
  if (error) throw error;
  
  const categories = (data || []).map(cat => cat.name);
  setCache(cacheKey, categories);
  return categories;
}

export async function fetchAllCoursesWithPromotions(): Promise<Course[]> {
  const cacheKey = "courses_with_promotions";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;

  const now = new Date().toISOString();
  
  // Fetch all courses with their promotions in a single query
  const { data: coursesData, error: coursesError } = await supabase
    .from("courses")
    .select(`
      *,
      promotion_courses!left(
        promotion:promotions!inner(
          id,
          name,
          discount_percentage,
          start_date,
          end_date,
          is_active
        )
      )
    `)
    .or("is_published.eq.true,is_coming_soon.eq.true,syllabus->>status.eq.coming_soon")
    .order("created_at", { ascending: false });

  if (coursesError) throw coursesError;

  // Fetch site-wide promotion separately
  const { data: siteWidePromo, error: promoError } = await supabase
    .from("promotions")
    .select(`
      id,
      name,
      discount_percentage,
      start_date,
      end_date,
      is_active,
      promotion_courses(course_id)
    `)
    .eq("is_active", true)
    .lte("start_date", now)
    .gte("end_date", now)
    .limit(1)
    .maybeSingle();

  if (promoError && promoError.code !== "PGRST116") throw promoError;

  // Check if site-wide promo has no specific courses
  const isSiteWide = siteWidePromo && (!siteWidePromo.promotion_courses || siteWidePromo.promotion_courses.length === 0);

  // Process courses with promotion data
  const courses = (coursesData || []).map((course: any) => {
    const coursePromos = course.promotion_courses || [];
    let discountPercentage = 0;

    // Check course-specific promotions first
    for (const pc of coursePromos) {
      if (pc.promotion && pc.promotion.is_active) {
        const startDate = new Date(pc.promotion.start_date);
        const endDate = new Date(pc.promotion.end_date);
        const currentDate = new Date();
        
        if (currentDate >= startDate && currentDate <= endDate) {
          discountPercentage = Math.max(discountPercentage, pc.promotion.discount_percentage);
        }
      }
    }

    // If no course-specific promo, apply site-wide
    if (discountPercentage === 0 && isSiteWide) {
      discountPercentage = siteWidePromo.discount_percentage;
    }

    // Remove the promotion_courses field from the final object
    const { promotion_courses, ...courseData } = course;

    return {
      ...courseData,
      discount_percentage: discountPercentage,
    };
  });

  setCache(cacheKey, courses);
  return courses;
}

export function clearCourseCache(): void {
  cache.clear();
}
